/**
 * queryState.ts -- 5-state discriminated union for composite query results.
 *
 * Every domain hook at apps/dashboard/src/api/hooks/ returns QueryState<T>.
 * The discriminant is `status` with branches: loading, empty, ready, error, forbidden.
 *
 * `wrapQuery` is the single helper that consumes `useQuery` output and produces
 * QueryState<T>. It catches ConvexError and maps `kind` values to the explicit
 * `forbidden` and `error` branches so the type contract matches runtime behaviour.
 *
 * Source contract: specs/prd.md SS11, v.1-DD SS4.3 Option A.
 */

/**
 * 5-state discriminated union for composite query results.
 *
 * - `loading`   -- query in flight, no data yet
 * - `empty`     -- query resolved but returned zero meaningful rows
 * - `ready`     -- query resolved with data
 * - `error`     -- query failed with a non-auth error
 * - `forbidden` -- query failed with an auth/membership error
 */
export type QueryState<T> =
  | { status: "loading" }
  | { status: "empty" }
  | { status: "ready"; data: T }
  | { status: "error"; error: string; signal?: string; retry: () => void }
  | { status: "forbidden"; reason: string };

/**
 * Transform raw useQuery output into a QueryState<TVM>.
 *
 * @param raw - The raw value returned by `useQuery` (undefined while loading)
 * @param transform - A pure function mapping the raw response to a view-model
 * @param isEmpty - Optional predicate; when it returns true for the transformed
 *   data, QueryState is `empty` instead of `ready`. Defaults to always false.
 */
export function wrapQuery<TRaw, TVM>(
  raw: TRaw | undefined,
  transform: (raw: TRaw) => TVM,
  isEmpty?: (data: TVM) => boolean,
): QueryState<TVM> {
  if (raw === undefined) {
    return { status: "loading" };
  }
  try {
    const data = transform(raw);
    if (isEmpty?.(data)) {
      return { status: "empty" };
    }
    return { status: "ready", data };
  } catch (err: unknown) {
    // Map ConvexError kind values to forbidden branch
    if (
      err != null &&
      typeof err === "object" &&
      "data" in err &&
      err.data != null &&
      typeof err.data === "object" &&
      "kind" in err.data
    ) {
      const kind = (err.data as { kind: string }).kind;
      if (
        kind === "unauthenticated" ||
        kind === "not-a-member" ||
        kind === "forbidden"
      ) {
        return { status: "forbidden", reason: kind };
      }
    }
    // Surface the error kind as a signal for the central error map
    let signal: string | undefined;
    if (
      err != null &&
      typeof err === "object" &&
      "data" in err &&
      err.data != null &&
      typeof err.data === "object" &&
      "kind" in err.data
    ) {
      signal = (err.data as { kind: string }).kind;
    }
    return {
      status: "error",
      error: err instanceof Error ? err.message : "Transform failed",
      signal,
      retry: () => {},
    };
  }
}

/**
 * Narrow a QueryState<TFull> into a QueryState<TSlice> by projecting the data
 * when in the `ready` branch. All other branches pass through unchanged.
 */
export function narrowContext<TFull, TSlice>(
  ctx: QueryState<TFull>,
  selector: (data: TFull) => TSlice,
): QueryState<TSlice> {
  switch (ctx.status) {
    case "loading":
      return { status: "loading" };
    case "empty":
      return { status: "empty" };
    case "error":
      return { status: "error", error: ctx.error, signal: ctx.signal, retry: ctx.retry };
    case "forbidden":
      return { status: "forbidden", reason: ctx.reason };
    case "ready":
      return { status: "ready", data: selector(ctx.data) };
  }
}
